import { BuildOutlined } from "@ant-design/icons";
import { PageHeader } from "@ant-design/pro-components";
import { Button, Input, Modal, Popconfirm, Select, message } from "antd";
import styles from "./UserQuestions.module.css";
import { DragDropContext, Droppable } from "react-beautiful-dnd";
import Break from "../../../Components/QuestionTypes/Break/Break";
import QuestionsMenu from "../../../Components/QuestionsMenu/QuestionsMenu";
import MultiOption from "../../../Components/QuestionTypes/MultiOption/MultiOption";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import ShortAnswer from "../../../Components/QuestionTypes/ShortAnswer/ShortAnswer";
import ProvinceAndCity from "../../../Components/QuestionTypes/ProvinceAndCity/ProvinceAndCity";
import DatePicker from "../../../Components/QuestionTypes/DatePicker/DatePicker";
import MultiDropdown from "../../../Components/QuestionTypes/MultiDropdown/MultiDropdown";
import MultiText from "../../../Components/QuestionTypes/MultiText/MultiText";
import Text from "../../../Components/QuestionTypes/Text/Text";
import api from "../../../api/api";
import { connect } from "react-redux";
import Loading from "../../../Components/Loading/Loading";
import { Link, useNavigate, useParams } from "react-router-dom";
import * as actions from "../../../store/actions/action";
import MediaManager from "../../../Components/QuestionTypes/Media/Media";
import { ConditionQuestionsContext } from "../../../Components/QuestionTypes/Question/ConditionQuestionsContext";
import ExportExcel from "../../../Util/ExportExcel";
import { buildFormQuestionsRows } from "../../../Util/FormQuestionsExcel";

const QuestionsManager = ({ role }) => {
  const { key } = useParams();
  const navigate = useNavigate();
  const [arrayOfQuestions, setArrayOfQuestions] = useState([]);
  const arrayOfQuestionsRef = useRef();
  const [newQuestionId, setNewQuestionId] = useState(0);
  const [newOptionId, setNewOptionId] = useState(0);
  const [updateForcer, setUpdateForcer] = useState(0);
  const [questionsTitle, setQuestionsTitle] = useState();
  const [collapse, setCollapse] = useState(false);
  const [formChanged, setFormChanged] = useState(false);
  const [questionsArray, setQuestionsArray] = useState([]);
  const [conditionQuestionIds, setConditionQuestionIds] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [accessType, setAccessType] = useState("product");
  const [accessModalOpen, setAccessModalOpen] = useState(false);
  const [accessLoading, setAccessLoading] = useState(false);
  const [accessRoles, setAccessRoles] = useState([]);
  const [accessRoleIds, setAccessRoleIds] = useState([]);
  const [canEditForm, setCanEditForm] = useState(true);

  const canManageAccess = role === "admin" || role === "apm";
  const frozenRoleIds = accessRoles
    .filter((r) => r.type === "admin" || r.type === "apm")
    .map((r) => r.id);

  const loadAccess = () => {
    setAccessLoading(true);
    api
      .get(`/forms/${key}/access`)
      .then((res) => {
        const roles = res.data.roles || [];
        setAccessRoles(roles);
        const frozen = roles
          .filter((r) => r.type === "admin" || r.type === "apm")
          .map((r) => r.id);
        setAccessRoleIds(
          Array.from(
            new Set([
              ...(res.data.viewer_roles || []),
              ...(res.data.editor_roles || []),
              ...frozen,
            ])
          )
        );
        setCanEditForm(res.data.canEdit !== false);
      })
      .catch(() => message.error("خطا در دریافت دسترسی‌ها"))
      .finally(() => setAccessLoading(false));
  };

  const openAccessModal = () => {
    setAccessModalOpen(true);
    loadAccess();
  };

  const saveAccess = () => {
    setAccessLoading(true);
    api
      .put(`/forms/${key}/access`, {
        viewer_roles: accessRoleIds,
        editor_roles: accessRoleIds,
      })
      .then(() => {
        message.success("دسترسی‌ها ذخیره شد");
        setAccessModalOpen(false);
      })
      .catch(() => message.error("خطا در ذخیره دسترسی‌ها"))
      .finally(() => setAccessLoading(false));
  };

  const loadQuestions = () => {
    setLoading(true);
    setLoadError(false);
    api
      .get(`/forms/${key}`)
      .then((res) => {
        setArrayOfQuestions(res.data.questions);
        setQuestionsTitle(res.data.title);
        setAccessType(res.data.access_type || "product");
        const ids = new Set();
        (res.data.conditions || []).forEach((block) => {
          (block.condition || []).forEach((c) => {
            if (c?.main?.questionId != null) ids.add(String(c.main.questionId));
          });
          (block.then || []).forEach((t) => {
            if (t?.target?.questionId != null) ids.add(String(t.target.questionId));
          });
        });
        setConditionQuestionIds(ids);
      })
      .catch(() => {
        setLoadError(true);
        message.error("خطا در دریافت پرسشنامه");
      })
      .finally(() => setLoading(false));

    api
      .get(`/forms/${key}`)
      .catch(() => message.error("خطا در دریافت پرسشنامه"))
      .then((res) => {
        const form = res.data.questions;
        setArrayOfQuestions(form);
        arrayOfQuestionsRef.current = JSON.parse(JSON.stringify(form));
        return form;
      })
      .then((questions) => {
        const allQuestionId = [].concat(
          ...questions.map((qa) => [
            qa.id,
            ...(qa.questions ?? []).map((a) => a.id),
          ])
        );

        const optionsIds = [];
        questions.forEach((q) => {
          q?.options?.forEach((opt) => opt.id);

          q?.questions?.forEach((subQ) => {
            optionsIds.push(...subQ.options.map((opt) => opt.id));
          });
        });

        setNewOptionId(Math.max(...optionsIds, 0) + 1);
        setNewQuestionId(Math.max(...allQuestionId, 0) + 1);
      });
  };
  const createNewForm = () => {
    setLoading(true);
    api
      .post("/forms", {
        title: questionsTitle,
        questions: arrayOfQuestions,
        access_type: accessType,
      })
      .then((res) => {
        message.success("با موفقیت ساخته شد");
        setFormChanged(false);
        navigate(`/question/${questionsTitle.trim().replaceAll(" ", "-")}`);
      })
      .catch(() => {
        message.error("خطا در ساخت پرسشنامه");
      })
      .finally(() => setLoading(false));
  };
  const updateForm = () => {
    setLoading(true);
    api
      .put(`/forms/${key}`, {
        title: questionsTitle,
        questions: arrayOfQuestions,
        access_type: accessType,
      })
      .then(() => {
        message.success("پرسشنامه با موفقیت آپدیت شد.");
        navigate(`/question/${questionsTitle.trim().replaceAll(" ", "-")}`);
        setFormChanged(false);
      })
      .catch(() => message.error("خطا در آپدیت پرسشنامه"))
      .finally(() => setLoading(false));
  };
  const saveHandler = () => {
    if (key) {
      updateForm();
    } else {
      createNewForm();
    }
  };
  const exportQuestionsExcel = () => {
    const rows = buildFormQuestionsRows(arrayOfQuestions);
    if (!rows.length) {
      message.warning("سوالی برای خروجی گرفتن وجود ندارد");
      return;
    }
    const fileName = `questions-${questionsTitle?.trim() || key}`;
    ExportExcel(rows, fileName, { rtl: true })
      .then(() => message.success("خروجی اکسل ساخته شد"))
      .catch(() => message.error("خطا در ساخت خروجی اکسل"));
  };
  const onChange = (field, questionId, value) => {
    setArrayOfQuestions((prev) =>
      prev.map((question) => {
        if (question.id !== questionId) return question;

        if (question.field_type !== "html") {
          setFormChanged(true);
        }
        return {
          ...question,
          [field]: value,
        };
      })
    );
  };
  const removeQuestion = (questionId) => {
    setFormChanged(true);
    const newArrayOfQuestions = [];
    JSON.parse(JSON.stringify(arrayOfQuestions)).forEach((question) => {
      if (question.id !== questionId) {
        newArrayOfQuestions.push(question);
      }
    });
    setArrayOfQuestions(newArrayOfQuestions);
  };
  const generateRandomNum = (len = 6) => Math.floor(Math.random() * 10 ** len);

  const duplicateQuestion = (questionId) => {
    setFormChanged(true);
    setArrayOfQuestions((prev) => {
      const questionIndex = prev.findIndex((e) => e.id === questionId);
      if (questionIndex === -1) return prev;
      message.success("سوال تکرار شد");

      const question = { ...prev[questionIndex] };
      question.id = generateRandomNum(6);
      question.options = (question.options ?? []).map((e) => ({
        ...e,
        id: generateRandomNum(6),
      }));
      if (Array.isArray(question.questions)) {
        question.questions = question.questions.map((e) => ({
          ...e,
          id: generateRandomNum(6),
          options: (e.options ?? []).map((el) => ({
            ...el,
            id: generateRandomNum(6),
          })),
        }));
      }

      const newArray = [
        ...prev.slice(0, questionIndex + 1),
        question,
        ...prev.slice(questionIndex + 1),
      ].map((e, innerIndex) => ({
        ...e,
        index: innerIndex,
        order: innerIndex,
      }));

      return newArray;
    });
  };

  const duplicateSubQuestion = (subQId) => {
    const mainQuestionEl = arrayOfQuestions?.find((e) =>
      e?.questions?.find((el) => el.id === subQId)
    );
    const subQuestionEl = mainQuestionEl?.questions?.find(
      (e) => e.id === subQId
    );

    if (!subQuestionEl || !mainQuestionEl) return false;
    setFormChanged(true);

    const newSub = {
      ...subQuestionEl,
      label: "",
      id: generateRandomNum(6),
      options: (subQuestionEl.options ?? []).map((e) => ({
        ...e,
        id: generateRandomNum(6),
      })),
    };

    const questions = [...mainQuestionEl.questions, newSub].map(
      (e, innerIndex) => ({
        ...e,
        index: innerIndex,
        order: innerIndex,
      })
    );

    onChange("questions", mainQuestionEl.id, questions);
  };

  const onDragEnd = (result) => {
    setFormChanged(true);
    const { destination, source, draggableId } = result;

    console.log({ destination, source, draggableId });
    if (
      source.droppableId === "questions" &&
      (questionsArray.length === 0
        ? true
        : destination.droppableId === "QuestionsContainer")
    ) {
      const newArrayOfQuestions = Array.from(arrayOfQuestions);

      newArrayOfQuestions.splice(destination?.index ?? 0, 0, {
        field_type: draggableId === "radio" ? "dropdown" : draggableId,
        id: newQuestionId,
        label: "",
      });
      setNewQuestionId((value) => value + 1);
      setArrayOfQuestions(newArrayOfQuestions);
    } else if (
      source.droppableId === "QuestionsContainer" &&
      destination.droppableId === "QuestionsContainer"
    ) {
      const newArrayOfQuestions = arrayOfQuestions.filter(
        (e) => e.id != draggableId
      );
      newArrayOfQuestions.splice(
        destination.index,
        0,
        arrayOfQuestions.find((e) => e.id == draggableId)
      );
      setArrayOfQuestions(newArrayOfQuestions);
    }
    setUpdateForcer(updateForcer + 1);
  };

  useEffect(() => {
    let questionsArrayTemp = [];
    if (Array.isArray(arrayOfQuestions)) {
      let noNumber = 0;
      for (let index = 0; index < arrayOfQuestions.length; index++) {
        const q = arrayOfQuestions[index];
        if (["html", "break"].indexOf(q.field_type) === -1) {
          noNumber += 1;
        }
        let questionDOM = null;
        const label = q.label?.trim();
        const name = q.id;
        const options = (q.options ?? [])?.map((opt) => ({
          ...opt,
          label: opt.label?.trim(),
          value: opt.id,
        }));

        const questions = (q?.questions ?? [])?.map((question) => ({
          label: question?.label?.trim(),
          value: question.id,
          ...question,
        }));

        const props = {
          ...q,
          label,
          options,
          noNumber,
          key: q.id,
          questions,
          name,
          index,
          newOptionId,
          newQuestionId,
          setNewOptionId,
          duplicateQuestion,
          duplicateSubQuestion,
          setNewQuestionId,
          onChange: (field, value) => onChange(field, q.id, value),
          onRemove: () => removeQuestion(q.id),
        };
        let emptyMultiQuestion = false;
        switch (q.field_type) {
          case "html":
            questionDOM = <Text {...props} />;
            break;
          case "break":
            questionDOM = <Break {...props} />;
            break;
          case "radio":
            questionDOM = <MultiOption {...props} />;
            break;
          case "checkboxes":
            questionDOM = <MultiOption {...props} />;
            break;
          case "dropdown":
            questionDOM = <MultiOption {...props} />;
            break;
          case "nps":
            questionDOM = <MultiOption {...props} />;
            break;
          case "number":
            questionDOM = <ShortAnswer {...props} type="number" />;
            break;
          case "city":
            questionDOM = <ProvinceAndCity {...props} />;
            break;
          case "date":
            questionDOM = <DatePicker {...props} />;
            break;
          case "text":
            questionDOM = <ShortAnswer {...props} />;
            break;
          case "multitext":
            // if (options.length <= 0) {
            //   emptyMultiQuestion = true;
            // }
            questionDOM = <MultiText {...props} />;
            break;
          case "multidropdown":
            // if (questions.length <= 0) {
            //   emptyMultiQuestion = true;
            // }
            questionDOM = <MultiDropdown {...props} />;
            break;
          case "paragraph":
            questionDOM = <ShortAnswer {...props} />;
            break;
          case "voice":
            questionDOM = <MediaManager {...props} />;
            break;
          case "image":
            questionDOM = <MediaManager {...props} />;
            break;

          default:
            questionDOM = <div>{q.field_type}</div>;
        }
        if (!emptyMultiQuestion) {
          questionsArrayTemp.push(questionDOM);
        }
      }
      setQuestionsArray(questionsArrayTemp);
    }
  }, [arrayOfQuestions]);

  useEffect(() => {
    if (key) {
      loadQuestions();
    } else {
      setArrayOfQuestions([]);
      setQuestionsTitle();
    }
  }, [key]);

  useEffect(() => {
    if (key && canManageAccess) {
      loadAccess();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, canManageAccess]);

  const desktopPreviewUrl = useMemo(() => {
    const url = new URL(
      `/app/question/preview?keyVal=preview_${key}`,
      process.env.REACT_APP_FRONTEND_BASEURL
    );
    return url.href;
  }, [key]);

  const mobilePreviewUrl = useMemo(() => {
    const url = new URL(
      `/app/question/preview?keyVal=preview_${key}&mobilePreview=true`,
      process.env.REACT_APP_FRONTEND_BASEURL
    );
    return url.href;
  }, [key]);

  useEffect(() => {
    console.log({
      base: process.env.REACT_APP_FRONTEND_BASEURL,
      desktopPreviewUrl,
      mobilePreviewUrl,
    });
  }, [desktopPreviewUrl, mobilePreviewUrl]);

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <PageHeader
        className={styles.QuestionsHeader}
        title={
          <>
            <Input
              size="medium"
              value={questionsTitle}
              onChange={(e) => setQuestionsTitle(e.target.value)}
              placeholder="نام پرسشنامه"
              style={{ width: "20rem" }}
            />
            <Select
              value={accessType}
              onChange={(v) => {
                setAccessType(v);
                setFormChanged(true);
              }}
              style={{ width: "12rem", marginRight: "0.5rem" }}
              options={[
                { value: "product", label: "متصل به محصول" },
                { value: "authenticated", label: "کاربران عضو" },
                { value: "public", label: "عمومی (همه)" },
              ]}
            />
          </>
        }
        avatar={{ icon: <BuildOutlined /> }}
        extra={
          <>
            {key && (
              <>
                {/* desktop */}
                {/* <Link href={desktopPreviewUrl} target="_blank" rel="noreferrer"> */}
                <Button
                  onClick={() => window.open(desktopPreviewUrl, "_blank")}
                >
                  پیش نمایش دسکتاپ
                </Button>
                {/* </Link> */}

                {/* mobile */}
                {/* <Link href={mobilePreviewUrl} target="_blank" rel="noreferrer"> */}
                <Button onClick={() => window.open(mobilePreviewUrl, "_blank")}>
                  پیش نمایش موبایل
                </Button>
                {/* </Link> */}
              </>
            )}
            <Button onClick={() => setCollapse((p) => !p)}>
              نمایش پنل سوالات
            </Button>
            {key && (
              <Link to={`/question/${key}/conditions`}>
                <Button>شروط سوالات</Button>
              </Link>
            )}
            {key && (
              <Link to={`/question/${key}/questions-to-flags`}>
                <Button>اتصالات کارت</Button>
              </Link>
            )}
            {key && canManageAccess && (
              <Button onClick={openAccessModal}>مدیریت دسترسی</Button>
            )}
            {key && (
              <Link to={`/question/${key}/responses`}>
                <Button>پاسخ‌ها</Button>
              </Link>
            )}
            {key && (
              <Button onClick={exportQuestionsExcel}>خروجی اکسل سوالات</Button>
            )}
            {key && (
              <Button
                onClick={() => {
                  const url = new URL(
                    `/form/${key}`,
                    process.env.REACT_APP_FRONTEND_BASEURL
                  ).href;
                  navigator.clipboard
                    .writeText(url)
                    .then(() => message.success("لینک فرم کپی شد"))
                    .catch(() => message.error("کپی لینک ناموفق بود"));
                }}
              >
                کپی لینک فرم
              </Button>
            )}
            <Popconfirm
              disabled={loading || (canManageAccess && !canEditForm)}
              onConfirm={saveHandler}
              title={`آیا از ${key ? "ثبت تغییر" : "ذخیره"} فرم مطمئن هستید؟`}
            >
              <Button
                disabled={loading || (canManageAccess && !canEditForm)}
                type="primary"
                className={[
                  formChanged ? styles.FlashingSaveButton : null,
                ].join(" ")}
              >
                {loading ? "در حال ذخیره" : key ? "ثبت تغییر" : "ذخیره"}
              </Button>
            </Popconfirm>
          </>
        }
      />
      <Modal
        title="مدیریت دسترسی فرم"
        open={accessModalOpen}
        onCancel={() => setAccessModalOpen(false)}
        onOk={saveAccess}
        okText="ذخیره"
        cancelText="انصراف"
        confirmLoading={accessLoading}
        okButtonProps={{ disabled: !canEditForm }}
      >
        <div>
          <label style={{ display: "block", marginBottom: 4 }}>
            نقش‌های دارای دسترسی
          </label>
          <Select
            mode="multiple"
            allowClear
            style={{ width: "100%" }}
            placeholder="انتخاب نقش‌ها"
            value={accessRoleIds}
            onChange={(vals) =>
              setAccessRoleIds(Array.from(new Set([...vals, ...frozenRoleIds])))
            }
            disabled={!canEditForm}
            options={accessRoles.map((r) => ({
              label: r.name,
              value: r.id,
              disabled: frozenRoleIds.includes(r.id),
            }))}
          />
        </div>
      </Modal>
      <div className={styles.UserQuestions}>
        <QuestionsMenu collapse={collapse} />
        <QuestionsContainer>
          <ConditionQuestionsContext.Provider value={conditionQuestionIds}>
            {loading ? (
              <Loading />
            ) : loadError ? (
              <div style={{ textAlign: "center", padding: "2rem", color: "#999" }}>
                <p>خطا در دریافت پرسشنامه.</p>
                <Button onClick={loadQuestions}>تلاش مجدد</Button>
              </div>
            ) : (
              questionsArray
            )}
          </ConditionQuestionsContext.Provider>
        </QuestionsContainer>
      </div>
    </DragDropContext>
  );
};

const QuestionsContainer = ({ children }) => {
  return (
    <Droppable droppableId="QuestionsContainer">
      {(provided) => {
        return (
          <div className={styles.QuestionsContainer}>
            <div ref={provided.innerRef} {...provided.droppableProps}>
              {children}
              {provided.placeholder}
            </div>
          </div>
        );
      }}
    </Droppable>
  );
};
const mapStateToProps = (state) => {
  return {
    token: state.token,
    role: state.userData ? state.userData.role : null,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    ClearError: () => dispatch(actions.ClearError()),
    ClearLoading: () => dispatch(actions.ClearLoading()),
    LoadingSetter: () => dispatch(actions.LoadingSetter()),
    ErrorSetter: () => dispatch(actions.ErrorSetter()),
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(memo(QuestionsManager));
